/**
 * Passives Tab Content for GameMenu
 * Responsive design - Passive skill tree view
 * Features: Respec button, search bar, node glow animations
 */

import { MenuTabContent } from '../MenuTabContent';
import { Player } from '@client/entities/Player';
import { PassiveNode } from '@shared/passives/PassiveTree';
import { t } from '@locales/index';

/** Base cost per refunded passive point, scales with player level */
const RESPEC_BASE_COST = 500;

export class PassivesTab extends MenuTabContent {
  private hoveredNode: string | null = null;
  private viewOffsetX: number = 0;
  private viewOffsetY: number = 0;
  private viewZoom: number = 1;
  private isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;

  // Respec state
  private respecMode: boolean = false;
  private respecCost: number = 0;

  // Search state
  private searchText: string = '';
  private searchMatches: Set<string> = new Set();
  private searchFocused: boolean = false;
  private cursorBlink: number = 0;

  // Animation time
  private time: number = 0;

  // Allocation animation effects
  private allocEffects: { nodeId: string; time: number }[] = [];

  // Mastery selection modal
  private showingMasteryChoice: string | null = null;

  constructor(player: Player) {
    super(player);
  }

  measureSize(_ctx: CanvasRenderingContext2D): { width: number; height: number } {
    return { width: 850, height: 500 };
  }

  update(deltaTime: number): void {
    this.time += deltaTime;
    this.cursorBlink += deltaTime;

    // Update allocation effects
    for (let i = this.allocEffects.length - 1; i >= 0; i--) {
      this.allocEffects[i].time += deltaTime;
      if (this.allocEffects[i].time >= 0.5) {
        this.allocEffects[i] = this.allocEffects[this.allocEffects.length - 1];
        this.allocEffects.pop();
      }
    }
  }

  onActivate(): void {
    this.hoveredNode = null;
    this.viewOffsetX = 0;
    this.viewOffsetY = 0;
    this.viewZoom = 1;
    this.respecMode = false;
    this.searchText = '';
    this.searchMatches.clear();
    this.searchFocused = false;
  }

  // Get responsive layout
  private getLayout() {
    const { width, height } = this.bounds;
    const padding = Math.min(20, width * 0.02);
    const scale = Math.min(width / 900, height / 550);

    return {
      padding,
      scale,
      fontSize: Math.max(10, Math.min(13, width / 90)),
      titleSize: Math.max(14, Math.min(18, width / 60)),
      nodeScale: Math.max(0.7, Math.min(1.2, scale)),
    };
  }

  /** Update search matches based on current searchText */
  private updateSearchMatches(): void {
    this.searchMatches.clear();
    if (this.searchText.length === 0) return;

    const query = this.searchText.toLowerCase();
    const nodes = this.player.passiveTree.getAllNodes();
    for (const node of nodes) {
      // Match against name
      if (node.name.toLowerCase().includes(query)) {
        this.searchMatches.add(node.id);
        continue;
      }
      // Match against description
      if (node.description && node.description.toLowerCase().includes(query)) {
        this.searchMatches.add(node.id);
        continue;
      }
      // Match against bonus stat names
      for (const stat of Object.keys(node.bonuses)) {
        const formatted = this.formatBonus(stat, 0).toLowerCase();
        if (formatted.includes(query)) {
          this.searchMatches.add(node.id);
          break;
        }
      }
    }
  }

  /** Calculate respec cost based on allocated nodes and player level */
  private calculateRespecCost(): number {
    const state = this.player.passiveTree.getState();
    let refundable = 0;
    for (const nodeId of state.allocatedNodes) {
      const node = this.player.passiveTree.getNode(nodeId);
      if (node && node.type !== 'start' && node.type !== 'gateway') {
        refundable++;
      }
    }
    // Cost scales with player level: base 500 * (1 + level/10)
    const levelMultiplier = 1 + this.player.level / 10;
    return Math.floor(refundable * RESPEC_BASE_COST * levelMultiplier);
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    const { x, y, width, height } = this.bounds;
    const layout = this.getLayout();

    // Tree area bounds (with padding for header/footer)
    const treeAreaY = y + 45;
    const treeAreaHeight = height - 75;

    const effectiveScale = layout.nodeScale * this.viewZoom;

    const centerX = x + width / 2 + this.viewOffsetX;
    const centerY = treeAreaY + treeAreaHeight / 2 + this.viewOffsetY;

    // Header with points
    const state = this.player.passiveTree.getState();
    const points = state.availablePoints;
    ctx.fillStyle = '#d4af37';
    ctx.font = `bold ${layout.titleSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(`${t('ui.passivePoints', { count: points })}`, x + layout.padding, y + 28);

    // Respec button
    this.renderRespecButton(ctx, layout);

    // Search bar
    this.renderSearchBar(ctx, layout);

    // Tree area background
    ctx.fillStyle = '#0d0a08';
    this.drawRoundedRect(ctx, x + layout.padding, treeAreaY, width - layout.padding * 2, treeAreaHeight, 8);
    ctx.fill();
    ctx.strokeStyle = '#3d3228';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Clip to tree area
    ctx.save();
    ctx.beginPath();
    ctx.rect(x + layout.padding, treeAreaY, width - layout.padding * 2, treeAreaHeight);
    ctx.clip();

    // Draw connections first
    const nodes = this.player.passiveTree.getAllNodes();
    ctx.lineWidth = 2;

    for (const node of nodes) {
      for (const connId of node.connections) {
        // Only draw each connection once (avoid duplicate lines)
        if (connId < node.id) continue;
        const conn = this.player.passiveTree.getNode(connId);
        if (conn) {
          const x1 = centerX + node.position.x * effectiveScale;
          const y1 = centerY + node.position.y * effectiveScale;
          const x2 = centerX + conn.position.x * effectiveScale;
          const y2 = centerY + conn.position.y * effectiveScale;

          const nodeAllocated = state.allocatedNodes.has(node.id);
          const connAllocated = state.allocatedNodes.has(conn.id);

          if (nodeAllocated && connAllocated) {
            ctx.strokeStyle = '#d4af37';
          } else if (nodeAllocated || connAllocated) {
            ctx.strokeStyle = '#6a5f35';
          } else {
            ctx.strokeStyle = '#3d3228';
          }

          // Fade non-matching connections during search
          if (this.searchText.length > 0) {
            const nodeMatch = this.searchMatches.has(node.id);
            const connMatch = this.searchMatches.has(conn.id);
            if (!nodeMatch && !connMatch) {
              ctx.globalAlpha = 0.2;
            }
          }

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }
      }
    }

    // Draw nodes
    for (const node of nodes) {
      this.renderNode(ctx, node, centerX + node.position.x * effectiveScale, centerY + node.position.y * effectiveScale, layout, effectiveScale);
    }

    // Draw allocation effects
    for (const effect of this.allocEffects) {
      const eNode = this.player.passiveTree.getNode(effect.nodeId);
      if (!eNode) continue;
      const ex = centerX + eNode.position.x * effectiveScale;
      const ey = centerY + eNode.position.y * effectiveScale;
      const t = effect.time / 0.5;
      const radius = 10 + t * 40;
      ctx.beginPath();
      ctx.arc(ex, ey, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(212, 175, 55, ${(1 - t) * 0.8})`;
      ctx.lineWidth = 2 * (1 - t);
      ctx.stroke();
    }

    // End clipping
    ctx.restore();

    // Tooltip (outside clip area so it's always visible)
    if (this.hoveredNode) {
      const node = this.player.passiveTree.getNode(this.hoveredNode);
      if (node) {
        this.renderTooltip(ctx, node, layout);
      }
    }

    // Mastery selection modal
    if (this.showingMasteryChoice) {
      this.renderMasteryModal(ctx, layout);
    }

    // Instructions
    ctx.fillStyle = '#505050';
    ctx.font = `${layout.fontSize - 1}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(t('passives.instructions'), x + width / 2, y + height - 12);
  }

  /** Render the Respec (Sifirla) button in the header area */
  private renderRespecButton(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width } = this.bounds;
    const btnW = 90;
    const btnH = 24;
    const btnX = x + width / 2 - btnW / 2;
    const btnY = y + 10;

    // Calculate cost for display
    this.respecCost = this.calculateRespecCost();

    // Button background
    if (this.respecMode) {
      ctx.fillStyle = '#8b2020';
    } else {
      ctx.fillStyle = '#3d2828';
    }
    this.drawRoundedRect(ctx, btnX, btnY, btnW, btnH, 4);
    ctx.fill();

    // Button border
    ctx.strokeStyle = this.respecMode ? '#ff4444' : '#6a4040';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Button text
    ctx.fillStyle = this.respecMode ? '#ff8888' : '#cc8888';
    ctx.font = `bold ${layout.fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (this.respecMode) {
      ctx.fillText(t('passives.confirm'), btnX + btnW / 2, btnY + btnH / 2);
    } else {
      ctx.fillText(t('passives.reset'), btnX + btnW / 2, btnY + btnH / 2);
    }

    // Cost tooltip below button when in respec mode
    if (this.respecMode && this.respecCost > 0) {
      ctx.fillStyle = 'rgba(12, 10, 8, 0.9)';
      const costText = t('passives.respecCost', { cost: this.respecCost });
      ctx.font = `${layout.fontSize - 1}px Arial`;
      const costW = ctx.measureText(costText).width + 12;
      this.drawRoundedRect(ctx, btnX + btnW / 2 - costW / 2, btnY + btnH + 2, costW, 16, 3);
      ctx.fill();
      ctx.fillStyle = '#ffd700';
      ctx.fillText(costText, btnX + btnW / 2, btnY + btnH + 10);
    }

    ctx.textBaseline = 'alphabetic';
  }

  /** Render the search input bar at top-right */
  private renderSearchBar(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const { x, y, width } = this.bounds;
    const barW = 160;
    const barH = 22;
    const barX = x + width - layout.padding - barW;
    const barY = y + 12;

    // Background
    ctx.fillStyle = this.searchFocused ? '#1a1815' : '#141210';
    this.drawRoundedRect(ctx, barX, barY, barW, barH, 4);
    ctx.fill();

    // Border
    ctx.strokeStyle = this.searchFocused ? '#d4af37' : '#3d3228';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Placeholder or typed text
    ctx.font = `${layout.fontSize}px Arial`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    if (this.searchText.length === 0 && !this.searchFocused) {
      ctx.fillStyle = '#606050';
      ctx.fillText(t('passives.search'), barX + 6, barY + barH / 2);
    } else {
      ctx.fillStyle = '#d0c8b0';
      // Clip text to bar width
      ctx.save();
      ctx.beginPath();
      ctx.rect(barX + 4, barY, barW - 8, barH);
      ctx.clip();
      ctx.fillText(this.searchText, barX + 6, barY + barH / 2);

      // Blinking cursor
      if (this.searchFocused && Math.sin(this.cursorBlink * 4) > 0) {
        const textW = ctx.measureText(this.searchText).width;
        ctx.fillStyle = '#d4af37';
        ctx.fillRect(barX + 6 + textW + 1, barY + 4, 1, barH - 8);
      }
      ctx.restore();
    }

    ctx.textBaseline = 'alphabetic';
  }

  private renderNode(
    ctx: CanvasRenderingContext2D,
    node: PassiveNode,
    x: number, y: number,
    layout: ReturnType<typeof this.getLayout>,
    effectiveScale: number = layout.nodeScale
  ): void {
    const baseSize = node.type === 'keystone' ? 30 : node.type === 'notable' ? 22 : node.type === 'mastery' ? 26 : node.type === 'jewel_socket' ? 24 : node.type === 'gateway' ? 12 : 16;
    const size = baseSize * effectiveScale;
    const isHovered = this.hoveredNode === node.id;
    const state = this.player.passiveTree.getState();
    const allocated = state.allocatedNodes.has(node.id);
    const canAllocate = this.player.passiveTree.canAllocate(node.id);

    // Search fade: if searching, dim non-matching nodes
    const isSearching = this.searchText.length > 0;
    const isMatch = this.searchMatches.has(node.id);
    if (isSearching && !isMatch) {
      ctx.globalAlpha = 0.2;
    }

    // Glow animation for allocated nodes
    if (allocated) {
      const glowAlpha = Math.sin(this.time * 2) * 0.15 + 0.85;
      ctx.globalAlpha = isSearching && !isMatch ? 0.2 : glowAlpha;

      // Glow ring behind node
      const glowSize = size * 1.4;
      const gradient = ctx.createRadialGradient(x, y, size * 0.3, x, y, glowSize);
      gradient.addColorStop(0, 'rgba(212, 175, 55, 0.25)');
      gradient.addColorStop(1, 'rgba(212, 175, 55, 0)');
      ctx.beginPath();
      ctx.arc(x, y, glowSize, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Keystone sparkle particles
      if (node.type === 'keystone') {
        const sparkleCount = 5;
        for (let i = 0; i < sparkleCount; i++) {
          const angle = (this.time * 1.5 + i * Math.PI * 2 / sparkleCount) % (Math.PI * 2);
          const dist = size * 0.8 + Math.sin(this.time * 3 + i) * 4;
          const sx = x + Math.cos(angle) * dist;
          const sy = y + Math.sin(angle) * dist;
          const sparkleAlpha = Math.sin(this.time * 4 + i * 1.5) * 0.4 + 0.4;
          ctx.fillStyle = `rgba(255, 215, 0, ${sparkleAlpha})`;
          ctx.beginPath();
          ctx.arc(sx, sy, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // Hovered node bright glow ring
    if (isHovered && (!isSearching || isMatch)) {
      const hoverGlowSize = size * 1.6;
      const hoverGrad = ctx.createRadialGradient(x, y, size * 0.2, x, y, hoverGlowSize);
      hoverGrad.addColorStop(0, 'rgba(255, 255, 200, 0.2)');
      hoverGrad.addColorStop(1, 'rgba(255, 255, 200, 0)');
      ctx.beginPath();
      ctx.arc(x, y, hoverGlowSize, 0, Math.PI * 2);
      ctx.fillStyle = hoverGrad;
      ctx.fill();
    }

    // Search match glow
    if (isSearching && isMatch && !allocated) {
      const matchGlowSize = size * 1.5;
      const matchGrad = ctx.createRadialGradient(x, y, size * 0.2, x, y, matchGlowSize);
      matchGrad.addColorStop(0, 'rgba(100, 200, 255, 0.3)');
      matchGrad.addColorStop(1, 'rgba(100, 200, 255, 0)');
      ctx.beginPath();
      ctx.arc(x, y, matchGlowSize, 0, Math.PI * 2);
      ctx.fillStyle = matchGrad;
      ctx.fill();
    }

    // Node background
    if (allocated) {
      if (node.type === 'mastery') {
        ctx.fillStyle = this.player.passiveTree.isMasteryUnlocked(node.id) ? '#ffaa00' : '#886600';
      } else if (node.type === 'jewel_socket') {
        ctx.fillStyle = '#00ccaa';
      } else if (node.type === 'gateway') {
        ctx.fillStyle = '#6688cc';
      } else {
        ctx.fillStyle = '#d4af37';
      }
    } else if (node.type === 'mastery') {
      ctx.fillStyle = this.player.passiveTree.isMasteryUnlocked(node.id) ? '#665500' : '#332a00';
    } else if (canAllocate && (node.type === 'gateway' || state.availablePoints > 0)) {
      ctx.fillStyle = isHovered ? '#6a5f35' : '#4a3f25';
    } else {
      ctx.fillStyle = '#2a2520';
    }

    ctx.beginPath();
    if (node.type === 'keystone') {
      // Hexagon shape
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const px = x + Math.cos(angle) * size;
        const py = y + Math.sin(angle) * size;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else if (node.type === 'notable') {
      // Diamond shape
      ctx.moveTo(x, y - size);
      ctx.lineTo(x + size, y);
      ctx.lineTo(x, y + size);
      ctx.lineTo(x - size, y);
      ctx.closePath();
    } else if (node.type === 'mastery') {
      // 8-pointed star
      for (let i = 0; i < 8; i++) {
        const angle = (Math.PI / 4) * i - Math.PI / 8;
        const r = i % 2 === 0 ? size : size * 0.55;
        const px = x + Math.cos(angle) * r;
        const py = y + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else if (node.type === 'jewel_socket') {
      ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    } else if (node.type === 'gateway') {
      const half = size * 0.35;
      ctx.rect(x - half, y - half, half * 2, half * 2);
    } else {
      ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    }
    ctx.fill();

    // Border
    ctx.strokeStyle = allocated ? '#ffd700' : isHovered ? '#8b7355' : '#4a3f35';
    ctx.lineWidth = node.type === 'keystone' ? 3 : 2;
    ctx.stroke();

    // Icon or initial
    if (allocated) {
      ctx.fillStyle = '#1a1510';
    } else {
      ctx.fillStyle = '#808070';
    }
    ctx.font = `bold ${size * 0.6}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(node.name.charAt(0), x, y);

    // Reset alpha
    ctx.globalAlpha = 1.0;
  }

  private renderTooltip(
    ctx: CanvasRenderingContext2D,
    node: PassiveNode,
    layout: ReturnType<typeof this.getLayout>
  ): void {
    const padding = 12;
    const lineH = Math.max(16, layout.fontSize + 4);
    const lines: { text: string; color: string }[] = [];

    lines.push({ text: node.name, color: '#d4af37' });

    if (node.description) {
      lines.push({ text: node.description, color: '#b0a090' });
    }

    // Format bonuses
    for (const [stat, value] of Object.entries(node.bonuses)) {
      if (typeof value === 'number') {
        const effectText = this.formatBonus(stat, value);
        lines.push({ text: effectText, color: '#8f8' });
      }
    }

    // Extra info for special node types
    if (node.type === 'mastery' && node.masteryData) {
      const unlocked = this.player.passiveTree.isMasteryUnlocked(node.id);
      if (unlocked) {
        const selected = this.player.passiveTree.getSelectedMastery(node.id);
        if (selected) {
          lines.push({ text: `${t('passives.selection')}: ${selected.name}`, color: '#ffcc44' });
        } else {
          lines.push({ text: t('passives.clickToSelect'), color: '#ffaa00' });
        }
      } else {
        lines.push({ text: t('passives.locked'), color: '#aa4444' });
      }
    } else if (node.type === 'jewel_socket') {
      const jewel = this.player.passiveTree.getInsertedJewel(node.id);
      if (jewel) {
        lines.push({ text: `${t('passives.jewel')}: ${jewel.name}`, color: '#00ffcc' });
      } else {
        lines.push({ text: t('passives.emptySocket'), color: '#666666' });
      }
    } else if (node.type === 'gateway') {
      lines.push({ text: t('passives.gateway'), color: '#88aaff' });
    }

    const state = this.player.passiveTree.getState();
    const allocated = state.allocatedNodes.has(node.id);

    if (node.type === 'mastery') {
      // Mastery nodes show unlock status instead
    } else if (!allocated) {
      const canAlloc = this.player.passiveTree.canAllocate(node.id);
      const hasPoints = node.type === 'gateway' || state.availablePoints > 0;
      if (!canAlloc) {
        lines.push({ text: t('passives.connectionRequired'), color: '#ff6666' });
      } else if (!hasPoints) {
        lines.push({ text: t('passives.noPoints'), color: '#ff6666' });
      }
    }

    ctx.font = `${layout.fontSize}px Arial`;
    let maxW = 0;
    for (const line of lines) {
      maxW = Math.max(maxW, ctx.measureText(line.text).width);
    }

    const w = maxW + padding * 2;
    const h = lines.length * lineH + padding * 2;
    const tx = this.bounds.x + this.bounds.width / 2 - w / 2;
    const ty = this.bounds.y + 55;

    ctx.fillStyle = 'rgba(12, 10, 8, 0.95)';
    this.drawRoundedRect(ctx, tx, ty, w, h, 6);
    ctx.fill();
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 1;
    ctx.stroke();

    let textY = ty + padding + 12;
    for (const line of lines) {
      ctx.fillStyle = line.color;
      ctx.font = line === lines[0] ? `bold ${layout.fontSize + 1}px Arial` : `${layout.fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(line.text, tx + w / 2, textY);
      textY += lineH;
    }
  }

  private formatBonus(stat: string, value: number): string {
    const statKeyMap: Record<string, string> = {
      flatHp: 'passiveStats.flatHp', flatMana: 'passiveStats.flatMana',
      flatArmor: 'passiveStats.flatArmor', flatEvasion: 'passiveStats.flatEvasion',
      flatStrength: 'passiveStats.flatStrength', flatAgility: 'passiveStats.flatAgility',
      flatSpirit: 'passiveStats.flatSpirit',
      percentDamage: 'passiveStats.percentDamage', percentAttackSpeed: 'passiveStats.percentAttackSpeed',
      percentCritChance: 'passiveStats.percentCritChance', percentCritDamage: 'passiveStats.percentCritDamage',
      percentLifeRegen: 'passiveStats.percentLifeRegen', percentManaRegen: 'passiveStats.percentManaRegen',
      percentHp: 'passiveStats.percentHp', percentMana: 'passiveStats.percentMana',
      percentArmor: 'passiveStats.percentArmor', percentEvasion: 'passiveStats.percentEvasion',
      percentPhysicalDamage: 'passiveStats.percentPhysicalDamage',
      percentElementalDamage: 'passiveStats.percentElementalDamage',
      percentMovementSpeed: 'passiveStats.percentMovementSpeed',
      percentCooldownReduction: 'passiveStats.percentCooldownReduction',
      percentHpRegen: 'passiveStats.percentHpRegen',
      lifeLeechPercent: 'passiveStats.lifeLeechPercent', manaLeechPercent: 'passiveStats.manaLeechPercent',
      percentFireDamage: 'passiveStats.percentFireDamage',
      percentLightningDamage: 'passiveStats.percentLightningDamage',
      percentColdDamage: 'passiveStats.percentColdDamage',
      percentDarkDamage: 'passiveStats.percentDarkDamage',
      dotDamage: 'passiveStats.dotDamage', minionDamage: 'passiveStats.minionDamage',
      minionLife: 'passiveStats.minionLife', areaOfEffect: 'passiveStats.areaOfEffect',
      projectileCount: 'passiveStats.projectileCount',
      extraDodgeCharges: 'passiveStats.extraDodgeCharges',
    };

    const key = statKeyMap[stat];
    const name = key ? t(key) : stat;
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value} ${name}`;
  }

  handleMouseEvent(e: import('../components/UIComponent').MouseEvent): boolean {
    if (!this.visible) return false;

    const { x, y, width } = this.bounds;
    const layout = this.getLayout();
    const effectiveScale = layout.nodeScale * this.viewZoom;

    // Tree area bounds (same as render)
    const treeAreaX = x + layout.padding;
    const treeAreaY = y + 45;
    const treeAreaWidth = width - layout.padding * 2;
    const treeAreaHeight = this.bounds.height - 75;

    const centerX = x + width / 2 + this.viewOffsetX;
    const centerY = treeAreaY + treeAreaHeight / 2 + this.viewOffsetY;
    const state = this.player.passiveTree.getState();

    // Respec button hit test
    const btnW = 90;
    const btnH = 24;
    const btnX = x + width / 2 - btnW / 2;
    const btnY = y + 10;

    if (e.type === 'down' && e.button === 'left') {
      // Check respec button click
      if (e.x >= btnX && e.x <= btnX + btnW && e.y >= btnY && e.y <= btnY + btnH) {
        if (this.respecMode) {
          // Confirm: perform respec
          const cost = this.calculateRespecCost();
          if (cost > 0 && this.player.getGold() >= cost) {
            this.player.inventory.removeGold(cost);
            this.player.passiveTree.resetTree();
            // Re-allocate start node
            this.player.passiveTree.allocateStartNode(this.player.className);
            this.player.recalculateStats();
          }
          this.respecMode = false;
        } else {
          this.respecMode = true;
        }
        return true;
      } else {
        // Clicking elsewhere cancels respec mode
        if (this.respecMode) {
          this.respecMode = false;
        }
      }

      // Check search bar click
      const barW = 160;
      const barH = 22;
      const barX = x + width - layout.padding - barW;
      const barY2 = y + 12;
      if (e.x >= barX && e.x <= barX + barW && e.y >= barY2 && e.y <= barY2 + barH) {
        this.searchFocused = true;
        this.cursorBlink = 0;
        return true;
      } else {
        this.searchFocused = false;
      }
    }

    // Check if mouse is within tree area
    const inTreeArea = e.x >= treeAreaX && e.x <= treeAreaX + treeAreaWidth &&
                       e.y >= treeAreaY && e.y <= treeAreaY + treeAreaHeight;

    // Scroll-zoom handling
    if (e.type === 'wheel' && e.deltaY !== undefined && inTreeArea) {
      if (e.deltaY > 0) {
        this.viewZoom *= 0.9;
      } else if (e.deltaY < 0) {
        this.viewZoom *= 1.1;
      }
      this.viewZoom = Math.max(0.4, Math.min(2.0, this.viewZoom));
      return true;
    }

    // Right-click to deallocate a single node
    if (e.type === 'down' && e.button === 'right' && inTreeArea) {
      const clickedNode = this.getNodeAt(e.x, e.y, centerX, centerY, layout, effectiveScale);
      if (clickedNode && state.allocatedNodes.has(clickedNode)) {
        if (this.player.passiveTree.deallocateNode(clickedNode)) {
          this.player.recalculateStats();
        }
        return true;
      }
    }

    if (e.type === 'down' && e.button === 'left' && inTreeArea) {
      // Handle mastery modal click
      if (this.showingMasteryChoice) {
        const handled = this.handleMasteryModalClick(e.x, e.y);
        if (handled) return true;
      }

      // Check for node click
      const clickedNode = this.getNodeAt(e.x, e.y, centerX, centerY, layout, effectiveScale);
      if (clickedNode) {
        const clickedNodeData = this.player.passiveTree.getNode(clickedNode);

        // Mastery node: open selection modal
        if (clickedNodeData?.type === 'mastery' && this.player.passiveTree.isMasteryUnlocked(clickedNode)) {
          this.showingMasteryChoice = clickedNode;
          return true;
        }

        if (this.player.passiveTree.canAllocate(clickedNode)) {
          this.player.passiveTree.allocateNode(clickedNode);
          this.allocEffects.push({ nodeId: clickedNode, time: 0 });
          this.player.recalculateStats();
        }
        return true;
      }

      // Start drag
      this.isDragging = true;
      this.lastMouseX = e.x;
      this.lastMouseY = e.y;
      return true;
    }

    if (e.type === 'move') {
      if (this.isDragging) {
        this.viewOffsetX += e.x - this.lastMouseX;
        this.viewOffsetY += e.y - this.lastMouseY;
        this.lastMouseX = e.x;
        this.lastMouseY = e.y;
      } else if (inTreeArea) {
        this.hoveredNode = this.getNodeAt(e.x, e.y, centerX, centerY, layout, effectiveScale);
      } else {
        this.hoveredNode = null;
      }
      return true;
    }

    if (e.type === 'up') {
      this.isDragging = false;
    }

    return false;
  }

  /** Handle keyboard input for search bar */
  handleKeyDown(key: string): boolean {
    // Close mastery modal on ESC
    if (key === 'Escape' && this.showingMasteryChoice) {
      this.showingMasteryChoice = null;
      return true;
    }

    if (!this.searchFocused) return false;

    if (key === 'Escape') {
      this.searchFocused = false;
      this.searchText = '';
      this.searchMatches.clear();
      return true;
    }

    if (key === 'Backspace') {
      if (this.searchText.length > 0) {
        this.searchText = this.searchText.slice(0, -1);
        this.updateSearchMatches();
      }
      return true;
    }

    // Only accept printable characters (single character keys)
    if (key.length === 1) {
      this.searchText += key;
      this.cursorBlink = 0;
      this.updateSearchMatches();
      return true;
    }

    return false;
  }

  private getNodeAt(
    mx: number, my: number,
    centerX: number, centerY: number,
    layout: ReturnType<typeof this.getLayout>,
    effectiveScale: number = layout.nodeScale
  ): string | null {
    const nodes = this.player.passiveTree.getAllNodes();
    for (const node of nodes) {
      const nx = centerX + node.position.x * effectiveScale;
      const ny = centerY + node.position.y * effectiveScale;
      const baseSize = node.type === 'keystone' ? 30 : node.type === 'notable' ? 22 : node.type === 'mastery' ? 26 : node.type === 'jewel_socket' ? 24 : node.type === 'gateway' ? 12 : 16;
      const size = baseSize * effectiveScale;
      const dx = mx - nx;
      const dy = my - ny;
      if (dx * dx + dy * dy <= (size / 2 + 5) * (size / 2 + 5)) {
        return node.id;
      }
    }
    return null;
  }

  // ========================
  // Mastery Selection Modal
  // ========================

  private renderMasteryModal(ctx: CanvasRenderingContext2D, layout: ReturnType<typeof this.getLayout>): void {
    const masteryId = this.showingMasteryChoice;
    if (!masteryId) return;
    const node = this.player.passiveTree.getNode(masteryId);
    if (!node?.masteryData) { this.showingMasteryChoice = null; return; }

    const { x, y, width, height } = this.bounds;
    const options = node.masteryData.options;
    const modalW = Math.min(280, width - 40);
    const optionH = 44;
    const padding = 12;
    const titleH = 30;
    const modalH = titleH + options.length * optionH + padding * 2;
    const modalX = x + (width - modalW) / 2;
    const modalY = y + (height - modalH) / 2;

    // Overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(x, y, width, height);

    // Modal background
    ctx.fillStyle = 'rgba(20, 18, 14, 0.98)';
    this.drawRoundedRect(ctx, modalX, modalY, modalW, modalH, 6);
    ctx.fill();
    ctx.strokeStyle = '#ffaa00';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Title
    ctx.fillStyle = '#ffaa00';
    ctx.font = `bold ${layout.fontSize + 2}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(node.name, modalX + modalW / 2, modalY + padding + 14);

    // Options
    const selectedId = this.player.passiveTree.getState().masterySelections.get(masteryId);
    ctx.textAlign = 'left';
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      const optY = modalY + titleH + i * optionH + padding;
      const isSelected = opt.id === selectedId;

      ctx.fillStyle = isSelected ? 'rgba(255, 170, 0, 0.15)' : 'rgba(40, 36, 28, 0.8)';
      this.drawRoundedRect(ctx, modalX + 6, optY, modalW - 12, optionH - 4, 4);
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#ffaa00' : '#554422';
      ctx.lineWidth = 1;
      ctx.stroke();

      const icon = opt.icon || '🌟';
      ctx.fillStyle = isSelected ? '#ffcc44' : '#ddcc88';
      ctx.font = `bold ${layout.fontSize}px Arial`;
      ctx.fillText(`${icon} ${opt.name}`, modalX + 14, optY + 16);

      ctx.fillStyle = '#aaa';
      ctx.font = `${layout.fontSize - 1}px Arial`;
      ctx.fillText(opt.description, modalX + 14, optY + 32);
    }

    ctx.textAlign = 'left';
  }

  private handleMasteryModalClick(mx: number, my: number): boolean {
    const masteryId = this.showingMasteryChoice;
    if (!masteryId) return false;
    const node = this.player.passiveTree.getNode(masteryId);
    if (!node?.masteryData) { this.showingMasteryChoice = null; return true; }

    const { x, y, width, height } = this.bounds;
    const options = node.masteryData.options;
    const modalW = Math.min(280, width - 40);
    const optionH = 44;
    const padding = 12;
    const titleH = 30;
    const modalH = titleH + options.length * optionH + padding * 2;
    const modalX = x + (width - modalW) / 2;
    const modalY = y + (height - modalH) / 2;

    // Check if click is on an option
    for (let i = 0; i < options.length; i++) {
      const optY = modalY + titleH + i * optionH + padding;
      if (mx >= modalX + 6 && mx <= modalX + modalW - 6 &&
          my >= optY && my <= optY + optionH - 4) {
        this.player.passiveTree.selectMasteryOption(masteryId, options[i].id);
        this.showingMasteryChoice = null;
        this.player.recalculateStats();
        return true;
      }
    }

    // Click outside modal closes it
    if (mx < modalX || mx > modalX + modalW || my < modalY || my > modalY + modalH) {
      this.showingMasteryChoice = null;
      return true;
    }

    return true;
  }
}
